import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL, retryAfterOf } from "@/lib/api";
import { STALE_MS, assessPolled, assessStreamed, displayAt } from "@/lib/freshness";

// One market-data feed for the whole app, independent of paper/live trading.
// Server aggregates Coinbase + Kraken + KuCoin exchange WebSockets (priority in that order) and fans quotes out over a
// public SSE stream; coins with no supported live pair are polled from CoinGecko. Every quote carries `at` (last update),
// `source`, `feed` (connected | disconnected | polled) and `state`: live | quiet | stale | polled | connecting | unavailable.
// Never shows an old price as live: LIVE needs a connected feed AND a trade for that asset within 60 s; a connected feed
// with no trade for >60 s is QUIET, >5 min is STALE; a venue silent for 15 s or a dropped stream makes every quote STALE.
const POLL_MS = 20000;
const FLUSH_MS = 400;
const SOURCE_LABEL = { coinbase: "EV-IDEX feed", kraken: "EV-IDEX feed", kucoin: "EV-IDEX feed", coingecko: "CoinGecko" };

const Ctx = createContext(null);

export function LiveMarketProvider({ children }) {
  const subs = useRef(new Map());            // key(SYM|id) -> {symbol, id, n}
  const buf = useRef({});                    // SYM -> {price, prev, at, change24, source, pair}
  const cg = useRef({});                     // id -> {price, at, change24}
  const sources = useRef(null);              // {symbols: {SYM: {sources:[{name,pair}]}}, owners: {SYM: cgid}}
  const health = useRef({ sources: {}, streamAt: null, streamOk: false });
  const skew = useRef(0);                    // client clock − server clock (ms); ages are measured against server time
  const es = useRef(null);
  const esSyms = useRef("");
  const reconnectTimer = useRef(null);
  const dirty = useRef(false);
  const pollRef = useRef(null);
  const pollTimer = useRef(null);
  const backoffUntil = useRef(0);        // provider Retry-After window: no fallback polls before this
  const [tick, setTick] = useState(0);

  const wantedSyms = () => {
    const m = sources.current?.symbols || {};
    return [...new Set([...subs.current.values()].filter((s) => s.symbol && m[s.symbol]).map((s) => s.symbol))].sort();
  };

  const openStream = useCallback(() => {
    const syms = wantedSyms();
    const key = syms.join(",");
    if (key === esSyms.current && es.current) return;
    esSyms.current = key;
    if (es.current) { es.current.close(); es.current = null; }
    if (!syms.length) return;
    const s = new EventSource(`${BACKEND_URL}/api/market/stream?symbols=${encodeURIComponent(key.toLowerCase())}`);
    es.current = s;
    const applyStatus = (st) => { if (st.t) skew.current = Date.now() - st.t; health.current = { ...health.current, sources: st.sources || {}, streamAt: Date.now(), streamOk: true }; dirty.current = true; };
    const applyTick = (t) => {
      const prev = buf.current[t.sym]?.price ?? t.price;
      buf.current[t.sym] = { price: t.price, prev, at: t.at, change24: t.change24 ?? buf.current[t.sym]?.change24 ?? null, source: t.source, pair: t.pair };
    };
    s.addEventListener("snapshot", (ev) => { const d = JSON.parse(ev.data); Object.values(d.quotes || {}).forEach(applyTick); applyStatus(d.status || {}); });
    s.addEventListener("ticks", (ev) => { const d = JSON.parse(ev.data); if (d.t) skew.current = Date.now() - d.t; d.ticks.forEach(applyTick); health.current.streamAt = Date.now(); health.current.streamOk = true; dirty.current = true; });
    s.addEventListener("status", (ev) => applyStatus(JSON.parse(ev.data)));
    s.onerror = () => { health.current = { ...health.current, streamOk: false }; dirty.current = true; };
  }, []);

  const schedule = useCallback(() => {
    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(openStream, 300); // debounce subscription churn into one reconnect
    clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(() => pollRef.current?.(), 1200);
  }, [openStream]);

  const subscribe = useCallback((items) => {
    const keys = [];
    for (const it of items) {
      const symbol = (it.symbol || "").toUpperCase(); const id = it.id || null;
      if (!symbol && !id) continue;
      const key = `${symbol}|${id || ""}`;
      const cur = subs.current.get(key);
      if (cur) cur.n += 1; else subs.current.set(key, { symbol, id, n: 1 });
      keys.push(key);
    }
    schedule();
    return () => {
      for (const k of keys) { const cur = subs.current.get(k); if (cur && --cur.n <= 0) subs.current.delete(k); }
      schedule();
    };
  }, [schedule]);

  // venue catalog (which symbols stream, and which CoinGecko id owns a shared symbol); retried until the feed has populated
  useEffect(() => {
    let alive = true, tries = 0;
    const load = async () => {
      try {
        const { data } = await api.get("/market/sources");
        if (!alive) return;
        sources.current = data;
        if (Object.keys(data.symbols || {}).length) { schedule(); return; }
      } catch (e) { console.warn("[apex] market sources:", e?.message || e); }
      if (alive && tries++ < 10) setTimeout(load, 3000);
    };
    load();
    const refresh = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(refresh); };
  }, [schedule]);

  // CoinGecko fallback for coins without a supported live pair (and streamed coins that have not printed yet).
  // Each quote keeps its OWN timestamp (never the response time), so a cached/stale quote is judged STALE, not POLLED;
  // when the server reports a provider back-off, polling pauses until that window has passed.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      const m = sources.current?.symbols;
      if (!m) return;
      if (Date.now() < backoffUntil.current) return;
      const ids = [...new Set([...subs.current.values()].filter((s) => s.id && !(s.symbol && m[s.symbol] && buf.current[s.symbol])).map((s) => s.id))].slice(0, 100);
      if (!ids.length) return;
      try {
        const { data } = await api.get("/market/prices", { params: { ids: ids.join(",") } });
        if (!alive) return;
        for (const [id, q] of Object.entries(data.quotes || {})) cg.current[id] = { price: q.price, change24: q.change24, at: q.at ?? data.t, source: "coingecko" };
        if (data.degraded) backoffUntil.current = Date.now() + Math.max(POLL_MS, (data.retry_after || 0) * 1000);
        dirty.current = true;
      } catch (e) {
        const ra = retryAfterOf(e) || (e?.response?.status === 503 ? 60 : 0);
        if (ra) backoffUntil.current = Date.now() + ra * 1000;
        console.warn("[apex] fallback quotes:", e?.message || e, ra ? `(backing off ${ra}s)` : "");
      }
    };
    pollRef.current = poll;
    const t = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(t); clearTimeout(pollTimer.current); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => { if (dirty.current) { dirty.current = false; setTick((x) => x + 1); } }, FLUSH_MS);
    const s = setInterval(() => setTick((x) => x + 1), 5000); // re-evaluate staleness even when nothing arrives
    return () => { clearInterval(t); clearInterval(s); };
  }, []);

  useEffect(() => () => { es.current?.close(); clearTimeout(reconnectTimer.current); }, []);

  const value = useMemo(() => {
    const now = Date.now();
    const srvNow = now - skew.current;
    const h = health.current;
    const streamLive = h.streamOk && h.streamAt && now - h.streamAt < STALE_MS;
    const sourceLive = (name) => { const s = h.sources?.[name]; return !!(streamLive && s && s.connected && s.last_msg_at && srvNow - s.last_msg_at < STALE_MS + 5000); };
    const owners = sources.current?.owners || {};
    const catalog = sources.current?.symbols || {};
    const allowed = (sym, id) => !!catalog[sym] && (!id || !owners[sym] || owners[sym] === id);
    // Per-asset freshness is judged separately from feed connectivity: a connected feed only makes a price LIVE if that
    // asset itself traded within QUIET_MS; thin markets go QUIET, then STALE after ASSET_STALE_MS. A down feed is always STALE.
    const quote = (symbol, id) => {
      const sym = (symbol || "").toUpperCase();
      const live = buf.current[sym];
      if (live && allowed(sym, id)) {
        const { age: _age, ...fresh } = assessStreamed({ at: live.at, feedUp: sourceLive(live.source), srvNow });
        return { ...live, ...fresh, at: displayAt(live.at, skew.current, now), sourceLabel: SOURCE_LABEL[live.source] || live.source };
      }
      const q = id ? cg.current[id] : null;
      if (q) { const { age: _age, ...fresh } = assessPolled({ at: q.at, srvNow }); return { ...q, ...fresh, at: displayAt(q.at, skew.current, now), prev: q.price, sourceLabel: "CoinGecko" }; }
      if (sym && catalog[sym] && allowed(sym, id)) return { price: null, at: null, source: catalog[sym].sources[0].name, sourceLabel: SOURCE_LABEL[catalog[sym].sources[0].name], state: streamLive ? "connecting" : "unavailable", feed: streamLive ? "connected" : "disconnected", reason: null };
      return null;
    };
    const feedSources = Object.fromEntries(Object.entries(h.sources || {}).map(([k, s]) => [k, { ...s, live: sourceLive(k), label: SOURCE_LABEL[k] || k }]));
    const anyLive = Object.values(feedSources).some((s) => s.live);
    return { subscribe, quote, onLive: (symbol, id) => allowed((symbol || "").toUpperCase(), id),
      feed: { connected: !!h.streamOk, live: !!anyLive, lastMsgAt: h.streamAt, sources: feedSources, source: anyLive ? "exchange" : h.streamOk ? "connecting" : "unavailable", staleAfterMs: STALE_MS }, tick };
  }, [subscribe, tick]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useLiveMarket = () => useContext(Ctx);

// items: [{symbol, id}] → { quotes: {SYM: quote}, byId: {id: quote}, feed }
export function useLiveQuotes(items = []) {
  const ctx = useContext(Ctx);
  const key = useMemo(() => items.map((i) => `${(i.symbol || "").toUpperCase()}|${i.id || ""}`).filter((k) => k !== "|").sort().join(","), [items]);
  useEffect(() => {
    if (!ctx || !key) return undefined;
    return ctx.subscribe(key.split(",").map((k) => { const [symbol, id] = k.split("|"); return { symbol, id: id || null }; }));
  }, [ctx, key]);
  return useMemo(() => {
    const quotes = {}, byId = {};
    if (ctx) for (const k of key ? key.split(",") : []) {
      const [symbol, id] = k.split("|");
      const q = ctx.quote(symbol, id || null);
      if (q) { if (symbol) quotes[symbol] = q; if (id) byId[id] = q; }
    }
    return { quotes, byId, feed: ctx?.feed || { connected: false, live: false, sources: {} }, onLive: ctx?.onLive || (() => false) };
  }, [ctx, key]);
}
